import React, { forwardRef, useEffect } from 'react';
import { FieldValue, RegisterOptions, Validate } from 'react-hook-form';
import FormInputValidator from '@ui/form/FormInputValidator';
import has from 'lodash/has';
import get from 'lodash/get';
import FormInputStyleProperties from '@ui/form/FormInputStyleProperties';
import { useTranslation } from '@ui/i18n/Translation';
import _uniqueId from 'lodash/uniqueId';
import ReactDatePicker from 'react-datepicker';
import Utilities from '@ui/utils/Utilities';

type Props = {
	isRequired?: boolean,
	placeholder?: string,
	form?: any,
	name: string,
	label?: string,
	labelHint?: React.JSX.Element,
	disabled?: boolean,
	extraClassName?: string,
	validation?: FormInputValidator | Validate<any, FieldValue<any>>,
	hideErrorMessage?: boolean,
	minDate?: Date
	maxDate?: Date
} & FormInputStyleProperties;


const FormInputDatePicker: React.FunctionComponent<Props> = (
	{
		form,
		placeholder,
		name,
		isRequired = false,
		label,
		isLoading,
		disabled = false,
		extraClassName,
		labelHint,
		validation,
		hideErrorMessage = false,
		minDate,
		maxDate,
	}) => {

	const { t } = useTranslation('common.form.error');
	const hasError = !!form && has(form.formState.errors, name);
	const errorMessage = hasError ? (get(form.formState.errors, name + '.message') || t('required')) : '';

	const CustomCalendarInput: React.FunctionComponent<{
		value?: any,
		onClick?: any,

	}> = forwardRef(({
										 value,
										 onClick,

									 }, ref: React.Ref<any>) => {
		return <input defaultValue={value} ref={ref}
									disabled={disabled}
									className={Utilities.classNames(
										'pl-10 input focus-visible:ring-inset',
										extraClassName,
										Utilities.NVL(hasError, 'input-error'),
										Utilities.NVL(disabled, 'input-disabled'),
									)}
									placeholder={placeholder}
									onClick={onClick}
		/>;
	});

	useEffect(() => {
		if (!disabled) {
			const options: Partial<RegisterOptions> = {
				required: isRequired,
			};
			if (!!validation) {
				if (typeof validation === 'function') {
					options.validate = validation;
				} else if (typeof validation === 'object') {
					options.validate = {
						checkUrl: (value) => {
							return (validation as FormInputValidator).check(value);
						},
					};
				}
			}
			form.register(name, options);
		}
	}, []);


	const value = form.watch(name);


	const uniqueId = _uniqueId('input-');

	const renderField = () => {
		return <>
			{!!form &&
				<ReactDatePicker id={uniqueId}
												 wrapperClassName="mt-0.5"
												 showIcon
												 showPopperArrow={false}
												 customInput={<CustomCalendarInput />}
												 dateFormat="dd/MM/yyyy"
												 calendarStartDay={1}
												 strictParsing
												 className={'h-full w-full rounded-md border-transparent bg-transparent py-0 pl-2 pr-7 text-slate-500 focus:border-transparent focus:ring-transparent sm:text-sm z-[11]'}
												 popperClassName={'z-[11]'}
												 disabled={disabled}
												 selected={!!value ? Utilities.parseISODate(value) : null}
												 minDate={minDate}
												 maxDate={maxDate}
												 isClearable
												 onChange={(date) => form.setValue(name, !!date ? Utilities.formatISODate(date) : null)} />}

		</>;
	};

	return <div>
		{!!label && <div className="label">{label}</div>}
		{!!labelHint ? labelHint : ''}
		{renderField()}
		{!!errorMessage && !hideErrorMessage && <p className="help help-error">{errorMessage}</p>}
	</div>;

};

export default FormInputDatePicker;